/**
 * Signal Command Center - NASA Control Room View
 * 
 * GLOBAL MASTER FEED showing NIFTY and BANKNIFTY simultaneously.
 * 
 * Layout:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ ZONE 1 (40%): DUAL ACTIVE TRADE CARDS                       │
 * │  ┌──────────────────┐  ┌──────────────────┐                 │
 * │  │   NIFTY 50       │  │   BANKNIFTY      │                 │
 * │  │   Scanning/Live  │  │   Scanning/Live  │                 │
 * │  └──────────────────┘  └──────────────────┘                 │
 * ├─────────────────────────────────────────────────────────────┤
 * │ ZONE 2 (60%): GLOBAL SIGNAL LOG (BOTH SYMBOLS)              │
 * │  - Time | Symbol | Signal | Score | Status                  │
 * │  - Mixed history sorted by time                             │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * PRODUCTION MODE:
 * - When market is CLOSED, displays "Last Known State" from final trading session
 * - NO mock data is ever shown to users
 * - Banner displays: "Market Closed. Displaying data from [Date] 15:30 IST"
 */

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Target, Clock, TrendingUp, TrendingDown, Activity, RefreshCw, AlertTriangle, Bell, BellOff, Ban, Zap, Flame } from 'lucide-react';
import { useMarket } from '../contexts/MarketProvider';

const API = process.env.REACT_APP_BACKEND_URL;

// Format duration from seconds
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '00:00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format time for log - SESSION 99e: Explicitly use IST timezone
// Session 147j+1: Some backend collections (e.g. lqp_signals) store
// timestamps as naive datetimes (UTC implicit but no `Z` marker on the
// JSON wire). JS's Date() then interprets them as LOCAL time and the
// dashboard shows UTC values. Fix: append `Z` when no timezone offset
// is present, so naive ISO strings are correctly parsed as UTC.
const formatTime = (timestamp) => {
  if (!timestamp) return '--:--';
  let ts = timestamp;
  if (typeof ts === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/.test(ts)) {
    ts = ts + 'Z';
  }
  const date = new Date(ts);
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata'  // SESSION 99e: Force IST timezone
  });
};

// Symbol Badge Component
const SymbolBadge = memo(({ symbol, source }) => {
  // SESSION 100: Support SMLT stock symbols in addition to NIFTY/BANKNIFTY
  // SESSION 104: Support IV Skew option symbols (e.g., "NIFTY 24300 CE")
  // SESSION 130: Support Alpha Strike option symbols (e.g., "NIFTY 23000CE") and Hero Zero
  const isNifty = symbol === 'NIFTY' || symbol === 'NIFTY-I';
  const isBNF = symbol === 'BANKNIFTY' || symbol === 'BANKNIFTY-I' || symbol === 'BNF';
  const isIVSkew = source === 'IV_SKEW' || (symbol && symbol.includes(' ') && (symbol.includes('CE') || symbol.includes('PE')) && source !== 'ALPHA_STRIKE' && source !== 'HERO_ZERO' && source !== 'CONFLUENCE');
  const isAlphaStrike = source === 'ALPHA_STRIKE';
  const isHeroZero = source === 'HERO_ZERO';
  const isConfluence = source === 'CONFLUENCE';
  // 146aj — informational pattern rows (LQP / DHOL / SLINGSHOT / POC_REJECT)
  const isPattern = typeof source === 'string' && source.startsWith('PATTERN_');
  const patternType = isPattern ? source.replace('PATTERN_', '') : null;
  
  // Determine display name and colors
  let displayName = symbol || 'UNKNOWN';
  let colorClass = 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';  // Default for stocks
  let prefix = null;
  
  if (isPattern) {
    // 146aj — pattern detections are informational; tealish-blue muted theme.
    // Prefix = setup type (LQP / DHOL / SS / POCR) so user can tell at a glance.
    displayName = symbol === 'BANKNIFTY' ? 'BNF' : symbol;
    colorClass = 'bg-teal-500/15 text-teal-300 border border-dashed border-teal-500/40';
    const shortMap = {
      LQP: 'LQP', DHOL: 'DHOL', SLINGSHOT: 'SS', SLINGSHOT_EP: 'SS',
      POC_REJECT: 'POCR', POC_REJECTION: 'POCR',
    };
    prefix = shortMap[patternType] || patternType;
  } else if (isConfluence) {
    // SESSION 137: Confluence composite signals — gold/amber theme with distinct styling
    displayName = symbol === 'BANKNIFTY' ? 'BNF' : symbol;
    colorClass = 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-300 border-2 border-amber-400/50';
    prefix = null;  // "High Probability Setup" label shown elsewhere
  } else if (isHeroZero) {
    // SESSION 130: Hero Zero signals - pink/magenta theme (explosive)
    displayName = symbol;
    colorClass = 'bg-pink-500/20 text-pink-400 border border-pink-500/30';
    prefix = 'HZ';
  } else if (isAlphaStrike) {
    // SESSION 132: Alpha Strike option signals - orange/flame theme
    displayName = symbol;  // Already formatted as "NIFTY 23000CE"
    colorClass = 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
    prefix = null;  // No prefix needed - show full symbol name
  } else if (isIVSkew) {
    // SESSION 104: IV Skew option signals - cyan/purple gradient theme
    // SESSION 128c: Show IVSA prefix
    displayName = symbol;  // Already formatted as "NIFTY 24300 CE"
    colorClass = 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30';
    prefix = 'IVSA';
  } else if (isNifty) {
    displayName = 'NIFTY';
    colorClass = 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
  } else if (isBNF) {
    displayName = 'BNF';
    colorClass = 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
  } else {
    // SMLT stocks - show actual symbol name
    displayName = symbol?.replace('-I', '') || symbol;
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-bold ${colorClass}`}>
      {prefix && <span className="mr-1 text-[10px]">{prefix}</span>}
      {displayName}
    </span>
  );
});

SymbolBadge.displayName = 'SymbolBadge';

// Compact Active Trade Card Component (for dual layout)
const ActiveTradeCardCompact = memo(({ trade, currentPrice, systemState, symbol }) => {
  const [duration, setDuration] = useState(0);
  const isNifty = symbol === 'NIFTY';
  const accentColor = isNifty ? 'cyan' : 'amber';
  
  // Update duration timer
  useEffect(() => {
    if (!trade?.entry_time) {
      setDuration(0);
      return;
    }
    
    const entryTime = new Date(trade.entry_time).getTime();
    const updateDuration = () => {
      const now = Date.now();
      setDuration(Math.floor((now - entryTime) / 1000));
    };
    
    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [trade?.entry_time]);
  
  // Calculate MTM P&L
  const calculateMTM = () => {
    if (!trade || !currentPrice || !trade.entry_price) return 0;
    const direction = trade.direction === 'LONG' ? 1 : -1;
    const lotSize = symbol === 'BANKNIFTY' ? 15 : 25;
    const priceDiff = (currentPrice - trade.entry_price) * direction;
    return priceDiff * lotSize;
  };
  
  const mtm = calculateMTM();
  const isLong = trade?.direction === 'LONG';
  
  // SCANNING STATE
  if (!trade) {
    return (
      <div className={`h-full flex flex-col items-center justify-center p-3 bg-gradient-to-b from-${accentColor}-500/5 to-transparent rounded-lg border border-${accentColor}-500/20`}>
        {/* Symbol Header */}
        <div className={`text-xs font-bold text-${accentColor}-400 uppercase mb-2`}>
          {symbol === 'BANKNIFTY' ? 'BANK NIFTY' : 'NIFTY 50'}
        </div>
        
        {/* Pulsing Scanner */}
        <div className="relative mb-2">
          <div className={`w-12 h-12 rounded-full border border-${accentColor}-500/30 animate-ping absolute inset-0`} />
          <div className={`w-12 h-12 rounded-full border-2 border-${accentColor}-500 flex items-center justify-center`}>
            <Target className={`w-6 h-6 text-${accentColor}-400 animate-pulse`} />
          </div>
        </div>
        
        <div className="text-sm font-bold text-foreground">SCANNING</div>
        <div className="text-[10px] text-muted-foreground">{systemState || 'NEUTRAL'}</div>
      </div>
    );
  }
  
  // ACTIVE TRADE STATE
  return (
    <div className={`h-full flex flex-col p-3 rounded-lg border-2 ${
      isLong ? 'bg-green-500/5 border-green-500/30' : 'bg-red-500/5 border-red-500/30'
    }`}>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-bold text-${accentColor}-400 uppercase`}>
          {symbol === 'BANKNIFTY' ? 'BANK NIFTY' : 'NIFTY 50'}
        </span>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded font-bold text-xs ${
          isLong ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {isLong ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {trade.direction}
        </div>
      </div>
      
      {/* Prices - SESSION 89: Explicit FUTURES labeling */}
      <div className="grid grid-cols-2 gap-2 mb-2 text-center">
        <div className="bg-surface/30 rounded p-1.5">
          <div className="text-[8px] text-muted-foreground uppercase">Entry <span className="text-cyan-400">(F)</span></div>
          <div className="text-sm font-bold font-mono">{trade.entry_price?.toFixed(2)}</div>
        </div>
        <div className="bg-surface/30 rounded p-1.5">
          <div className="text-[8px] text-muted-foreground uppercase">Current <span className="text-cyan-400">(F)</span></div>
          <div className="text-sm font-bold font-mono">{currentPrice?.toFixed(2)}</div>
        </div>
      </div>
      
      {/* MTM P&L */}
      <div className={`flex-1 flex flex-col items-center justify-center rounded p-2 ${
        mtm >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'
      }`}>
        <div className="text-[8px] text-muted-foreground uppercase">Live MTM</div>
        <div className={`text-xl font-bold font-mono ${mtm >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {mtm >= 0 ? '+' : ''}₹{mtm.toFixed(0)}
        </div>
      </div>
      
      {/* Duration */}
      <div className="flex items-center justify-center gap-1 mt-2 text-[10px] text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span className="font-mono">{formatDuration(duration)}</span>
      </div>
    </div>
  );
});

ActiveTradeCardCompact.displayName = 'ActiveTradeCardCompact';

// Session Summary Badge Component (Clickable for filtering)
const SessionSummaryBadge = memo(({ icon: Icon, label, count, colorClass, isActive, onClick, filterType }) => (
  <button 
    onClick={() => onClick(filterType)}
    className={`flex items-center gap-2.5 px-4 py-2 rounded-lg border transition-all duration-200 ${colorClass} ${
      isActive 
        ? 'ring-2 ring-offset-1 ring-offset-[#0b1120] scale-105 shadow-lg' 
        : 'hover:scale-102 hover:brightness-110 opacity-80 hover:opacity-100'
    }`}
    title={`Click to ${isActive ? 'clear filter' : `filter by ${label}`}`}
  >
    <Icon className="w-4 h-4" />
    <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    <span className="text-base font-bold">{count}</span>
  </button>
));

SessionSummaryBadge.displayName = 'SessionSummaryBadge';

// Global Signal Log Table Component
const GlobalSignalLogTable = memo(({ logs, isLoading }) => {
  const scrollRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(null); // 'longs' | 'shorts' | null
  
  // Handle filter toggle
  const handleFilterClick = useCallback((filterType) => {
    setActiveFilter(prev => prev === filterType ? null : filterType);
  }, []);
  
  // Auto-scroll to bottom when new logs arrive (only if no filter active)
  useEffect(() => {
    if (scrollRef.current && !activeFilter) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs?.length, activeFilter]);
  
  // SESSION 90: Use logs directly - they're already filtered by parent
  const validLogs = logs || [];
  
  // Calculate session summary metrics - SESSION 90: Only count valid signals
  // SESSION 132: Renamed ORB to Alpha Strike
  const sessionMetrics = React.useMemo(() => {
    if (!validLogs || validLogs.length === 0) {
      return { freshLongs: 0, freshShorts: 0, alphaStrikeSignals: 0, heroZeroSignals: 0, ivSkewSignals: 0 };
    }
    
    let freshLongs = 0;
    let freshShorts = 0;
    let alphaStrikeSignals = 0;
    let heroZeroSignals = 0;
    let ivSkewSignals = 0;
    let confluenceSignals = 0;
    
    validLogs.forEach(log => {
      if (log.source === 'ALPHA_STRIKE') {
        alphaStrikeSignals++;
      } else if (log.source === 'HERO_ZERO') {
        heroZeroSignals++;
      } else if (log.source === 'IV_SKEW') {
        ivSkewSignals++;
      } else if (log.source === 'CONFLUENCE') {
        confluenceSignals++;
      }
      // Apr-2026: Bucket by EFFECTIVE market direction, not signal label.
      // For option signals, `signal='BUY'` on a PE is directionally SHORT
      // (you profit if underlying drops). Counting by raw signal label
      // misled users during expiry days when PE BUY signals dominated.
      //   CE BUY / PE SELL → effective LONG
      //   PE BUY / CE SELL → effective SHORT
      //   Index BUY / SELL → direct mapping
      const sym = (log.symbol || '').toUpperCase();
      const isCE = / CE\b/.test(sym) || sym.endsWith('CE');
      const isPE = / PE\b/.test(sym) || sym.endsWith('PE');
      let effective = log.signal;  // default: direct mapping (for index signals)
      if (isCE && log.signal === 'BUY') effective = 'BUY';
      else if (isCE && log.signal === 'SELL') effective = 'SELL';
      else if (isPE && log.signal === 'BUY') effective = 'SELL';   // long PE = short bias
      else if (isPE && log.signal === 'SELL') effective = 'BUY';   // short PE = long bias
      if (effective === 'BUY') {
        freshLongs++;
      } else if (effective === 'SELL') {
        freshShorts++;
      }
    });
    
    return { freshLongs, freshShorts, alphaStrikeSignals, heroZeroSignals, ivSkewSignals, confluenceSignals };
  }, [validLogs]);
  
  // Filter logs based on active filter - SESSION 90: Use validLogs
  // SESSION 132: Renamed 'orb' filter to 'alphastrike'
  const filteredLogs = React.useMemo(() => {
    if (!activeFilter || !validLogs) return validLogs;
    
    return validLogs.filter(log => {
      switch (activeFilter) {
        case 'longs':
          return log.signal === 'BUY';
        case 'shorts':
          return log.signal === 'SELL';
        case 'alphastrike':
          // SESSION 128c: OPTIONS filter includes Alpha Strike and IV Skew signals
          return log.source === 'ALPHA_STRIKE' || log.source === 'IV_SKEW';
        case 'herozero':
          return log.source === 'HERO_ZERO';
        default:
          return true;
      }
    });
  }, [validLogs, activeFilter]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-accent animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading global signal feed...</span>
      </div>
    );
  }
  
  if (!logs || logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Activity className="w-12 h-12 mb-2 opacity-50" />
        <div className="text-sm">No signals recorded today</div>
        <div className="text-xs mt-1">NIFTY & BANKNIFTY signals will appear here</div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* SESSION SUMMARY BAR (Clickable Filters) */}
      <div className="flex items-center gap-4 px-4 py-3 bg-[#0b1120] border-b border-gray-800">
        <SessionSummaryBadge 
          icon={TrendingUp}
          label="Fresh Longs"
          count={sessionMetrics.freshLongs}
          colorClass="bg-green-500/10 border-green-500/30 text-green-400 ring-green-400"
          isActive={activeFilter === 'longs'}
          onClick={handleFilterClick}
          filterType="longs"
        />
        <SessionSummaryBadge 
          icon={TrendingDown}
          label="Fresh Shorts"
          count={sessionMetrics.freshShorts}
          colorClass="bg-red-500/10 border-red-500/30 text-red-400 ring-red-400"
          isActive={activeFilter === 'shorts'}
          onClick={handleFilterClick}
          filterType="shorts"
        />
        <SessionSummaryBadge 
          icon={Zap}
          label="OPTIONS"
          count={sessionMetrics.alphaStrikeSignals + sessionMetrics.ivSkewSignals}
          colorClass="bg-orange-500/10 border-orange-500/30 text-orange-400 ring-orange-400"
          isActive={activeFilter === 'alphastrike'}
          onClick={handleFilterClick}
          filterType="alphastrike"
        />
        <SessionSummaryBadge 
          icon={Flame}
          label="Hero Zero"
          count={sessionMetrics.heroZeroSignals}
          colorClass="bg-pink-500/10 border-pink-500/30 text-pink-400 ring-pink-400"
          isActive={activeFilter === 'herozero'}
          onClick={handleFilterClick}
          filterType="herozero"
        />
        <div className="ml-auto flex items-center gap-3">
          {activeFilter && (
            <button 
              onClick={() => setActiveFilter(null)}
              className="text-xs text-cyan-400 hover:text-cyan-300 underline"
            >
              Clear Filter
            </button>
          )}
          <span className="text-xs text-muted-foreground">
            {activeFilter ? `Showing: ${filteredLogs.length}` : `Total: ${validLogs.length}`} signals
          </span>
        </div>
      </div>
      
      {/* SCROLLABLE TABLE */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin">
        <table className="w-full text-sm">
          {/* SESSION 111: Updated columns - replaced Exit Time/Exit Price with T1/T2/SL */}
          <thead className="sticky top-0 z-20" style={{ backgroundColor: '#0b1120' }}>
            <tr className="text-muted-foreground uppercase border-b-2 border-gray-700">
              <th className="text-left py-3 px-3 font-semibold bg-[#0b1120]">Time</th>
              <th className="text-left py-3 px-3 font-semibold bg-[#0b1120]">Symbol</th>
              <th className="text-left py-3 px-3 font-semibold bg-[#0b1120]">Signal</th>
              <th className="text-center py-3 px-3 font-semibold bg-[#0b1120]">
                <span className="cursor-help" title="God Score: Directional signal strength (0-100)&#10;IVSA: Z-score (IV dislocation magnitude)&#10;R:R = Reward to Risk ratio. E.g., 2.5:1 means for every 1 INR risked, the potential reward is 2.5 INR">Score</span>
              </th>
              <th className="text-right py-3 px-3 font-semibold bg-[#0b1120]">Entry</th>
              <th className="text-right py-3 px-3 font-semibold bg-[#0b1120]">
                <span className="text-green-400">T1</span>
              </th>
              <th className="text-right py-3 px-3 font-semibold bg-[#0b1120]">
                <span className="text-blue-400">T2</span>
              </th>
              <th className="text-right py-3 px-3 font-semibold bg-[#0b1120]">
                <span className="text-red-400">SL</span>
              </th>
              <th className="text-right py-3 px-3 font-semibold bg-[#0b1120]">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log, idx) => {
              const isVetoed = log.status === 'BLOCKED' || log.status === 'VETOED';
              const isExecuted = log.status === 'EXECUTED' || log.status === 'OPEN' || log.status === 'CLOSED';
              const isClosed = log.status === 'CLOSED';
              
              // SESSION 111: Format price with ₹ symbol (Indian locale)
              const formatPrice = (price, forceShow = false) => {
                if ((!price || price === 0) && !forceShow) return '--';
                if (!price || price === 0) return '--';
                return price.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              };
              
              // Entry price: Show for OPEN/CLOSED, "--" for BLOCKED
              const entryDisplay = isVetoed ? '--' : formatPrice(log.entry_price);
              
              // SESSION 111: T1/T2/SL from API response
              const t1Display = formatPrice(log.t1);
              const t2Display = formatPrice(log.t2);
              const slDisplay = formatPrice(log.sl);
              
              // SESSION 111: Status display with exit reason
              const statusDisplay = log.status_display || log.status;
              
              return (
                <tr 
                  key={log.id || idx}
                  data-testid={`signal-row-${idx}`}
                  className={`border-b border-border/20 hover:bg-surface/30 transition-colors ${
                    isVetoed ? 'opacity-60' : ''
                  } ${
                    log.source === 'IV_SKEW' ? 'bg-gradient-to-r from-cyan-500/5 to-purple-500/5' : ''
                  } ${
                    log.source === 'ALPHA_STRIKE' ? 'bg-orange-500/5' : ''
                  } ${
                    log.source === 'CONFLUENCE' ? 'bg-gradient-to-r from-amber-500/8 to-yellow-500/5 border-l-2 border-l-amber-400/60' : ''
                  }`}
                >
                  {/* Time */}
                  <td className="py-3 px-3 font-mono text-muted-foreground whitespace-nowrap">
                    {formatTime(log.timestamp)}
                  </td>
                
                {/* Symbol Badge */}
                <td className="py-3 px-3">
                  <SymbolBadge symbol={log.symbol} source={log.source} />
                </td>
                
                {/* Signal Type */}
                <td className="py-3 px-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold ${
                      log.signal === 'BUY' ? 'bg-green-500/20 text-green-400' :
                      log.signal === 'SELL' ? 'bg-red-500/20 text-red-400' :
                      isVetoed ? 'bg-orange-500/20 text-orange-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {isVetoed && <Ban className="w-3.5 h-3.5" />}
                      {isExecuted && log.signal === 'BUY' && <TrendingUp className="w-3.5 h-3.5" />}
                      {isExecuted && log.signal === 'SELL' && <TrendingDown className="w-3.5 h-3.5" />}
                      {log.signal || 'WAIT'}
                    </span>
                    {log.source === 'CONFLUENCE' && (
                      <span className="text-[9px] font-bold text-amber-300 bg-amber-500/15 border border-amber-400/30 px-1.5 py-0.5 rounded" data-testid="high-probability-badge">
                        High Probability Setup
                      </span>
                    )}
                    {log.is_informational && (
                      <span
                        className="text-[9px] font-bold text-teal-300 bg-teal-500/15 border border-teal-400/30 px-1.5 py-0.5 rounded"
                        data-testid={`pattern-info-badge-${log.id}`}
                        title={`${log.pattern_type || 'Pattern'} detected — informational only.\nNot wired to Telegram or broker order yet.\nUse this to validate setups before promoting to live emission.`}
                      >
                        Info Only
                      </span>
                    )}
                  </div>
                </td>
                
                {/* Score - SESSION 104: Show Z-score for IV_SKEW signals */}
                {/* SESSION 132: Show range size for Alpha Strike signals, multiplier for Hero Zero */}
                {/* SESSION 128c: Show IVSA prefix for IV Skew, R:R for Alpha Strike */}
                <td className="py-3 px-3 text-center">
                  {log.source === 'IV_SKEW' ? (
                    <span className={`font-mono font-bold text-sm px-2 py-1 rounded ${
                      log.score < -1.5 ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' :
                      log.score > 1.5 ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                      'text-foreground'
                    }`} title={`IV Dislocation: ${log.score >= 0 ? '+' : ''}${log.score?.toFixed(1)}σ from SVI fair value\nReward:Risk = ${log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 ? ((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1) : 'N/A'}:1\nFor every 1 INR risked, potential reward is ${log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 ? ((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1) : 'N/A'} INR`}>
                      IVSA {log.score >= 0 ? '+' : ''}{log.score?.toFixed(1) || '0'}σ
                      {log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 && (
                        <span className="ml-1.5 text-xs text-yellow-400/80">
                          R:R {((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1)}:1
                        </span>
                      )}
                    </span>
                  ) : log.source === 'ALPHA_STRIKE' ? (
                    <span className="text-orange-400 font-mono text-xs" title={`${log.side_reason || ''}\nR:R ${log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 ? ((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1) : '?'}:1`}>
                      R:R {log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 ? 
                        ((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1) : 
                        log.score}:1
                      {log.pop > 0 && (
                        <span 
                          className={`ml-1.5 cursor-help ${
                            log.pop >= 70 ? 'text-green-400' :
                            log.pop >= 60 ? 'text-yellow-400' :
                            'text-gray-400'
                          }`}
                          title={`Probability of Profit: ${log.pop}%\nBased on IV regime${log.side_reason ? ' (' + log.side_reason.split('—')[0].trim() + ')' : ''}, God Score alignment (${log.score || '?'}), and R:R ratio.\nHigher POP = stronger edge.${log.side_tag === 'SECONDARY' ? '\n\nSECONDARY signal — preferred side has higher POP.' : ''}`}
                          data-testid={`pop-${log.id}`}
                        >
                          POP {log.pop}%
                        </span>
                      )}
                    </span>
                  ) : log.source === 'HERO_ZERO' ? (
                    <span className="text-pink-400 font-mono text-xs" title={`Score: ${log.score} | Peak: ${log.peak_premium?.toFixed(1)}`}>
                      HZ {log.multiplier ? `${log.multiplier.toFixed(1)}x` : `S:${log.score}`}
                    </span>
                  ) : log.source === 'CONFLUENCE' ? (
                    <span className="font-mono text-xs" title={`Confluence Score: ${log.confluence_score}\nPattern: ${log.confluence_pattern}\nR:R ${log.risk_reward}:1 — For every 1 unit risked, potential reward is ${log.risk_reward} units.\n${log.is_high_conviction ? 'HIGH CONVICTION' : 'Moderate conviction'}`}>                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 font-bold ${
                        log.confluence_score >= 80 ? 'text-green-300 bg-green-500/15 border-green-400/40' :
                        'text-amber-300 bg-amber-500/15 border-amber-400/40'
                      }`}>
                        {log.confluence_score}
                      </span>
                      {log.risk_reward > 0 && (
                        <span className="ml-1 text-cyan-400">R:R {log.risk_reward}:1</span>
                      )}
                    </span>
                  ) : (
                    <span className={`font-mono font-bold text-base ${
                      log.score > 60 ? 'text-green-400' :
                      log.score < -60 ? 'text-red-400' :
                      'text-foreground'
                    }`} title={log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 ? `God Score: ${log.score >= 0 ? '+' : ''}${log.score?.toFixed(0)}\nR:R ${((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1)}:1 — For every 1 INR risked, potential reward is ${((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1)} INR` : `God Score: ${log.score >= 0 ? '+' : ''}${log.score?.toFixed(0)}`}>
                      {log.score >= 0 ? '+' : ''}{log.score?.toFixed(0) || '--'}
                      {log.t1 && log.sl && log.entry_price && Math.abs(log.entry_price - log.sl) > 0 && (
                        <span className="ml-1 text-xs text-yellow-400/80 font-medium">
                          ({((Math.abs(log.t1 - log.entry_price)) / Math.abs(log.entry_price - log.sl)).toFixed(1)}:1)
                        </span>
                      )}
                    </span>
                  )}
                </td>
                
                {/* Entry Price Column */}
                <td className="py-3 px-3 text-right">
                  <span className={`font-mono text-sm ${
                    isExecuted && !isVetoed ? 'text-cyan-400' : 'text-muted-foreground'
                  }`}>
                    {entryDisplay}
                  </span>
                </td>
                
                {/* SESSION 111: T1 Column */}
                <td className="py-3 px-3 text-right">
                  <span className={`font-mono text-sm ${
                    isClosed && statusDisplay.includes('T1') ? 'text-green-400 font-bold' : 'text-green-400/70'
                  }`}>
                    {t1Display}
                  </span>
                </td>
                
                {/* SESSION 111: T2 Column */}
                <td className="py-3 px-3 text-right">
                  <span className="font-mono text-sm text-blue-400/70">
                    {t2Display}
                  </span>
                </td>
                
                {/* SESSION 111: SL Column */}
                <td className="py-3 px-3 text-right">
                  <span className={`font-mono text-sm ${
                    isClosed && statusDisplay.includes('SL') ? 'text-red-400 font-bold' : 'text-red-400/70'
                  }`}>
                    {slDisplay}
                  </span>
                </td>
                
                {/* SESSION 111: Status with exit reason */}
                <td className="py-3 px-3 text-right">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded whitespace-nowrap ${
                    log.status === 'OPEN' ? 'bg-cyan-500/20 text-cyan-400' :
                    statusDisplay.includes('T1') ? 'bg-green-500/20 text-green-400' :
                    statusDisplay.includes('SL') ? 'bg-red-500/20 text-red-400' :
                    statusDisplay.includes('EOD') ? 'bg-purple-500/20 text-purple-400' :
                    log.status === 'CLOSED' ? 'bg-purple-500/20 text-purple-400' :
                    log.status === 'BLOCKED' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {statusDisplay}
                    {(log.pnl !== undefined || log.pnl_points !== undefined) && log.status === 'CLOSED' && (
                      <span className={`ml-1.5 ${(log.pnl ?? log.pnl_points) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(log.pnl ?? log.pnl_points) >= 0 ? '+' : ''}{(log.pnl ?? log.pnl_points)?.toFixed(1)}
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
});

GlobalSignalLogTable.displayName = 'GlobalSignalLogTable';

// Main Signal Command Center Component - NASA Control Room
export const SignalCommandCenter = memo(({ 
  niftyPrice = 0,
  bankniftyPrice = 0,
  niftyPulse = null,
  bankniftyPulse = null,
  niftyTrade = null,
  bankniftyTrade = null
}) => {
  const [globalLog, setGlobalLog] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTrades, setActiveTrades] = useState({ NIFTY: null, BANKNIFTY: null });
  const [marketStatus, setMarketStatus] = useState({ isOpen: true, dataType: 'LIVE', lastUpdated: null });
  
  // === AUDIO ALERTS (Session 39) ===
  const { audioEnabled, toggleAudio, connectionStatus } = useMarket();
  
  // Fetch market status and last known state
  const fetchMarketStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await fetch(`${API}/api/market/last-known-state`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setMarketStatus({
            isOpen: data.is_market_open ?? false,
            dataType: data.data_type || 'UNKNOWN',
            lastUpdated: data.states?.NIFTY?.last_updated || data.states?.BANKNIFTY?.last_updated || null,
            message: data.message || null
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch market status:', err);
    }
  }, []);
  
  // Fetch global signal log (all symbols - NIFTY, BANKNIFTY + SMLT stocks + Alpha Strike options)
  const fetchGlobalLog = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch indices, SMLT signals, Alpha Strike signals, Hero Zero signals, and IV Skew signals in parallel
      const [niftyRes, bnfRes, smltRes, alphaStrikeRes, heroZeroRes, ivSkewRes, confluenceRes, patternsRes] = await Promise.all([
        fetch(`${API}/api/signals/today?symbol=NIFTY`, { headers }),
        fetch(`${API}/api/signals/today?symbol=BANKNIFTY`, { headers }),
        fetch(`${API}/api/signals/today/all`, { headers }),  // SESSION 100: All signals including SMLT
        fetch(`${API}/api/alpha-strike/signals`, { headers }),  // SESSION 132: Alpha Strike Options signals
        fetch(`${API}/api/hero-zero/signals`, { headers }),  // SESSION 130: Hero Zero signals
        fetch(`${API}/api/iv-skew/signals`, { headers }),  // SESSION 128c: IV Skew signals
        fetch(`${API}/api/advanced-signals/confluence/signals`, { headers }),  // SESSION 137: Composite signals
        fetch(`${API}/api/patterns/today?hide_closed=true`, { headers }),  // 146al: Hide closed (SL/TGT-hit) pattern fires — only show currently-pending. 146aj: Path A — informational LQP / DHOL / SLINGSHOT_EP / POC_REJECT rows
      ]);
      
      const niftyData = niftyRes.ok ? await niftyRes.json() : { signals: [], open_symbols: [] };
      const bnfData = bnfRes.ok ? await bnfRes.json() : { signals: [], open_symbols: [] };
      const smltData = smltRes.ok ? await smltRes.json() : { signals: [], open_symbols: [] };
      const alphaStrikeData = alphaStrikeRes.ok ? await alphaStrikeRes.json() : { signals: [] };
      const heroZeroData = heroZeroRes.ok ? await heroZeroRes.json() : { signals: [] };
      const ivSkewData = ivSkewRes.ok ? await ivSkewRes.json() : { signals: [] };
      const confluenceData = confluenceRes.ok ? await confluenceRes.json() : { signals: { NIFTY: [], BANKNIFTY: [] } };
      const patternsData = patternsRes.ok ? await patternsRes.json() : { patterns: [] };
      
      // SESSION 111: Get open symbols from all responses
      const openSymbolsSet = new Set([
        ...(niftyData.open_symbols || []),
        ...(bnfData.open_symbols || []),
        ...(smltData.open_symbols || [])
      ]);
      
      // Combine all signals - use Set to avoid duplicates
      const seenIds = new Set();
      // Apr-2026: Also dedupe by (symbol|timestamp|signal) so that the same
      // signal emitted from /signals/today + /iv-skew/signals (different ID
      // schemes) doesn't appear twice. Each source assigns its own id, but
      // the underlying signal is the same row in daily_signals.
      const seenSemanticKeys = new Set();
      const semanticKey = (sym, ts, dir) => `${(sym || '').trim()}|${ts || ''}|${dir || ''}`;
      const combined = [];
      
      // Add NIFTY signals
      for (const s of (niftyData.signals || [])) {
        const semKey = semanticKey(s.symbol, s.timestamp, s.signal);
        if (!seenIds.has(s.id) && !seenSemanticKeys.has(semKey)) {
          seenIds.add(s.id);
          seenSemanticKeys.add(semKey);
          combined.push(s);
        }
      }
      
      // Add BANKNIFTY signals
      for (const s of (bnfData.signals || [])) {
        const semKey = semanticKey(s.symbol, s.timestamp, s.signal);
        if (!seenIds.has(s.id) && !seenSemanticKeys.has(semKey)) {
          seenIds.add(s.id);
          seenSemanticKeys.add(semKey);
          combined.push(s);
        }
      }
      
      // Add SMLT stock signals (filter out NIFTY/BANKNIFTY duplicates)
      for (const s of (smltData.signals || [])) {
        const semKey = semanticKey(s.symbol, s.timestamp, s.signal);
        if (!seenIds.has(s.id) && !seenSemanticKeys.has(semKey) && s.symbol !== 'NIFTY' && s.symbol !== 'BANKNIFTY' && s.symbol !== 'NIFTY-I' && s.symbol !== 'BANKNIFTY-I') {
          seenIds.add(s.id);
          seenSemanticKeys.add(semKey);
          combined.push(s);
        }
      }
      
      // SESSION 132: Add Alpha Strike Options signals
      // Transform Alpha Strike signal format to match standard signal format
      for (const sig of (alphaStrikeData.signals || [])) {
        const sigId = `AS_${sig.index}_${sig.option_type}_${sig.strike}_${sig.direction}_${sig.signal_generated_at}`;
        const asSymbol = `${sig.index} ${sig.strike}${sig.option_type}`;
        const asTs = `${sig.date}T${sig.signal_generated_at}`;
        const asDir = sig.direction === 'BUY' ? 'BUY' : 'SELL';
        const asSemKey = semanticKey(asSymbol, asTs, asDir);
        if (!seenIds.has(sigId) && !seenSemanticKeys.has(asSemKey)) {
          seenIds.add(sigId);
          seenSemanticKeys.add(asSemKey);
          combined.push({
            id: sigId,
            symbol: `${sig.index} ${sig.strike}${sig.option_type}`,
            signal: sig.direction === 'BUY' ? 'BUY' : 'SELL',
            timestamp: `${sig.date}T${sig.signal_generated_at}`,
            entry_price: sig.entry_price,
            t1: sig.t1,
            t2: sig.t2,
            sl: sig.sl,
            score: Math.round(sig.range_size),  // Use range size as "score" indicator
            status: sig.outcome || 'OPEN',
            source: 'ALPHA_STRIKE',  // Mark as Alpha Strike signal for special styling
            phase: sig.phase,  // 'shadow' or 'live'
            is_expiry_day: sig.is_expiry_day,
            range_high: sig.range_high,
            range_low: sig.range_low,
            // SESSION 136: Smart Side Selection data
            side_confidence: sig.side_confidence,
            side_reason: sig.side_reason,
            side_tag: sig.side_tag,
            pop: sig.pop,
            // Exit fields
            exit_price: sig.exit_price,
            exit_time: sig.exit_time,
            exit_reason: sig.exit_reason,
            pnl_points: sig.pnl,
          });
        }
      }
      
      // SESSION 130: Add Hero Zero signals
      // Transform Hero Zero signal format to match standard signal format
      for (const hz of (heroZeroData.signals || [])) {
        const hzId = `HZ_${hz.symbol}_${hz.trigger_time}`;
        const hzTs = `${hz.date}T${hz.trigger_time}`;
        const hzSemKey = semanticKey(hz.symbol, hzTs, 'BUY');
        if (!seenIds.has(hzId) && !seenSemanticKeys.has(hzSemKey)) {
          seenIds.add(hzId);
          seenSemanticKeys.add(hzSemKey);
          combined.push({
            id: hzId,
            symbol: hz.symbol,  // Already formatted like "NIFTY 23000 CE"
            signal: 'BUY',  // Hero Zero is always BUY (catching the blast)
            timestamp: `${hz.date}T${hz.trigger_time}`,
            entry_price: hz.entry_premium,
            t1: hz.entry_premium * 2,  // 2x target
            t2: hz.entry_premium * 3,  // 3x target
            sl: hz.entry_premium * 0.5,  // 50% SL
            score: Math.round(hz.hero_zero_score),
            status: hz.outcome || 'OPEN',
            source: 'HERO_ZERO',  // Mark as Hero Zero signal
            phase: hz.phase,  // blast phase
            triggers: hz.triggers,  // List of triggers that fired
            multiplier: hz.multiplier,
            peak_premium: hz.peak_premium
          });
        }
      }
      
      // SESSION 128c: Add IV Skew Analyzer (IVSA) signals
      // Transform IV Skew signal format to match standard signal format
      for (const ivs of (ivSkewData.signals || [])) {
        const ivsId = `IVSA_${ivs.underlying}_${ivs.strike}_${ivs.option_type}_${ivs.timestamp}`;
        const ivsSymbol = `${ivs.underlying} ${ivs.strike}${ivs.option_type}`;
        const ivsSemKey = semanticKey(ivsSymbol, ivs.timestamp, ivs.direction);
        if (!seenIds.has(ivsId) && !seenSemanticKeys.has(ivsSemKey)) {
          seenIds.add(ivsId);
          seenSemanticKeys.add(ivsSemKey);
          combined.push({
            id: ivsId,
            symbol: `${ivs.underlying} ${ivs.strike}${ivs.option_type}`,
            signal: ivs.direction,  // BUY or SELL
            timestamp: ivs.timestamp,
            entry_price: ivs.entry_price || ivs.ltp,
            t1: ivs.t1,
            t2: ivs.t2,
            sl: ivs.sl,
            score: ivs.z_score,  // Z-score
            status: ivs.status || 'OPEN',
            source: 'IV_SKEW',  // Mark as IV Skew signal for special styling
            actual_iv: ivs.actual_iv,
            expected_iv: ivs.expected_iv,
            residual: ivs.residual,
            // SESSION 136: Exit fields for closed signals
            exit_price: ivs.exit_price,
            exit_time: ivs.exit_time,
            exit_reason: ivs.exit_reason,
            outcome: ivs.outcome,
            pnl_points: ivs.pnl,
          });
        }
      }
      
      // SESSION 137: Add Confluence Composite signals (High Probability Setups)
      const allConfluenceSignals = [
        ...(confluenceData.signals?.NIFTY || []),
        ...(confluenceData.signals?.BANKNIFTY || []),
      ];
      for (const cs of allConfluenceSignals) {
        const csId = `CONF_${cs.symbol}_${cs.pattern}_${cs.timestamp}`;
        const csDir = cs.direction === 'LONG' ? 'BUY' : 'SELL';
        const csSemKey = semanticKey(cs.symbol, cs.timestamp, csDir);
        if (!seenIds.has(csId) && !seenSemanticKeys.has(csSemKey)) {
          seenIds.add(csId);
          seenSemanticKeys.add(csSemKey);
          combined.push({
            id: csId,
            symbol: cs.symbol,
            signal: cs.direction === 'LONG' ? 'BUY' : 'SELL',
            timestamp: cs.timestamp,
            entry_price: cs.entry_price,
            t1: cs.target_1,
            sl: cs.stop_loss,
            score: cs.confluence_score,
            status: 'OPEN',
            source: 'CONFLUENCE',
            confluence_pattern: cs.pattern,
            confluence_score: cs.confluence_score,
            risk_reward: cs.risk_reward,
            is_high_conviction: cs.is_high_conviction,
            god_score_at_emit: cs.god_score_at_emit,
            day_type: cs.day_type,
          });
        }
      }

      // 146aj — Path A: Add LQP / DHOL / SLINGSHOT_EP / POC_REJECT pattern
      // detections as INFORMATIONAL rows in the Global Signal Log so the
      // user can see whether each setup fired today even though they are
      // not (yet) wired to Telegram / broker order. These rows are tagged
      // `source: "PATTERN_<TYPE>"` and `is_informational: true` so the
      // PnL / outcome columns can render an em-dash instead of pretending
      // to be an executed trade.
      // The backend `_enrich_patterns_with_signal_fields` ensures every
      // row has uniform `entry_price`, `target_1`, `target_2`, `stop_loss`
      // fields (synthesised from SYMBOL_DEFAULTS when the detector didn't
      // persist them), so each row reads like a tradeable signal.
      for (const p of (patternsData.patterns || [])) {
        const ptype = (p.pattern || '').toUpperCase();
        // Only show the 4 actionable setups; skip noise types like
        // ABSORPTION/EXHAUSTION/STACKED_IMBALANCE that fire on every candle.
        if (!['LQP', 'DHOL', 'SLINGSHOT', 'SLINGSHOT_EP',
              'POC_REJECT', 'POC_REJECTION'].includes(ptype)) {
          continue;
        }
        // Pattern direction can be BUY/SELL or BULLISH/BEARISH/LONG/SHORT.
        const rawDir = (p.direction || '').toUpperCase();
        const dir = ['BUY', 'BULLISH', 'LONG'].includes(rawDir) ? 'BUY'
                    : ['SELL', 'BEARISH', 'SHORT'].includes(rawDir) ? 'SELL'
                    : rawDir;
        const det = p.details || {};
        const ts = p.timestamp || `${p.date}T${p.time}`;
        const pId = `PAT_${ptype}_${p.symbol}_${ts}_${dir}`;
        const pSemKey = semanticKey(p.symbol, ts, dir);
        if (!seenIds.has(pId) && !seenSemanticKeys.has(pSemKey)) {
          seenIds.add(pId);
          seenSemanticKeys.add(pSemKey);
          combined.push({
            id: pId,
            symbol: p.symbol,
            signal: dir,
            timestamp: ts,
            // 146aj — backend now enriches `details` with uniform keys.
            entry_price: det.entry_price,
            t1: det.target_1,
            t2: det.target_2,
            sl: det.stop_loss,
            score: Math.round(p.confidence || 0),
            status: p.outcome ? 'CLOSED' : 'OPEN',
            outcome: p.outcome,
            source: `PATTERN_${ptype}`,
            pattern_type: ptype,
            is_informational: true,  // signals UI not to fake PnL columns
            details: det,
          });
        }
      }

      // Sort by timestamp
      combined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // SESSION 91: Pre-filter BLOCKED/VETO signals from API response
      // This keeps the UI clean and shows only actionable signals
      const filteredCombined = combined.filter(signal => 
        signal.status !== 'BLOCKED' && 
        signal.status !== 'VETOED' && 
        signal.signal !== 'VETO'
      );
      
      // SESSION 136: Normalize T1/SL/EOD statuses to CLOSED for option signals
      // Backend lifecycle sets status to "T1", "SL", "EOD" but frontend only recognizes "OPEN"/"CLOSED"
      const CLOSED_STATUSES = new Set(['T1', 'SL', 'T1_HIT', 'SL_HIT', 'EOD', 'MANUAL', 'CLOSED']);
      for (const signal of filteredCombined) {
        if (CLOSED_STATUSES.has(signal.status) && signal.status !== 'CLOSED') {
          signal.exit_reason = signal.exit_reason || signal.status;
          signal.status_display = `CLOSED (${signal.status})`;
          signal.status = 'CLOSED';
        }
      }
      
      // SESSION 111: Track symbols with OPEN positions
      // We'll filter out new signals for symbols that already have an OPEN position
      const symbolsWithOpenPositions = new Set();
      
      // SESSION 86u + 99e: Link CLOSE signals to their original BUY/SELL entries
      // When a CLOSE signal exists, update the original entry's status
      // SESSION 117c FIX: Don't re-process signals that already have status_display from backend
      const processedSignals = [];
      
      // Separate entry signals and close signals
      const entrySignals = filteredCombined.filter(s => s.signal === 'BUY' || s.signal === 'SELL');
      const closeSignals = filteredCombined.filter(s => s.signal === 'CLOSE' && s.status === 'CLOSED');
      
      // Sort both by timestamp
      entrySignals.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      closeSignals.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // SESSION 99e: Create a queue of closes per symbol for FIFO matching
      const closeQueues = new Map(); // symbol -> array of closes
      closeSignals.forEach(close => {
        if (!closeQueues.has(close.symbol)) {
          closeQueues.set(close.symbol, []);
        }
        closeQueues.get(close.symbol).push({
          exit_price: close.exit_price,
          pnl: close.pnl,
          reason: close.reason,
          close_time: close.timestamp,
          exit_reason: close.exit_reason || close.lifecycle_exit_reason
        });
      });
      
      // Process entries and match with closes in FIFO order
      entrySignals.forEach(signal => {
        // SESSION 117c FIX: If backend already closed this signal with pnl_points, use that data
        // Don't re-match with CLOSE signals which can cause wrong P&L display
        if (signal.status === 'CLOSED' && signal.pnl_points !== undefined) {
          // Backend already processed this - use its data directly
          const exitReason = signal.exit_reason || signal.lifecycle_exit_reason || '';
          const reasonDisplay = exitReason ? exitReason.toUpperCase().replace('_', ' ') : '';
          processedSignals.push({
            ...signal,
            status_display: signal.status_display || (reasonDisplay ? `CLOSED (${reasonDisplay})` : 'CLOSED'),
            pnl: signal.pnl_points,  // Use backend's pnl_points, not re-calculated
          });
          return;  // Don't try to match with CLOSE signals
        }
        
        const symbolCloses = closeQueues.get(signal.symbol) || [];
        
        // SESSION 118 FIX: Match CLOSE signals with validation
        // Must match:
        // 1. Same symbol
        // 2. Close time AFTER entry time
        // 3. Direction matches (BUY->LONG, SELL->SHORT)
        // 4. Entry price approximately matches
        const signalDir = signal.signal === 'BUY' ? 'LONG' : 'SHORT';
        const signalEntry = signal.entry_price || 0;
        
        const closeIndex = symbolCloses.findIndex(close => {
          const closeTime = new Date(close.close_time);
          const entryTime = new Date(signal.timestamp);
          const timeMatch = closeTime > entryTime;
          
          // Check direction matches
          const closeDir = close.direction || '';
          const directionMatch = (closeDir === signalDir) || 
                                 (closeDir === 'UP' && signalDir === 'LONG') ||
                                 (closeDir === 'DOWN' && signalDir === 'SHORT') ||
                                 !closeDir; // Allow if direction not specified (legacy)
          
          // Check entry price approximately matches (within 10 points)
          const closeEntry = close.entry_price || 0;
          const priceMatch = !closeEntry || !signalEntry || Math.abs(closeEntry - signalEntry) < 10;
          
          return timeMatch && directionMatch && priceMatch;
        });
        
        if (closeIndex !== -1 && signal.status === 'OPEN') {
          const closeData = symbolCloses[closeIndex];
          // Remove this close from the queue so it doesn't match again
          symbolCloses.splice(closeIndex, 1);
          
          // SESSION 111: Use exit_reason for status_display
          const exitReason = closeData.exit_reason || '';
          const reasonDisplay = exitReason ? exitReason.toUpperCase().replace('_', ' ') : '';
          
          processedSignals.push({
            ...signal,
            status: 'CLOSED',
            status_display: reasonDisplay ? `CLOSED (${reasonDisplay})` : 'CLOSED',
            exit_price: closeData.exit_price,
            exit_time: closeData.close_time,
            pnl: closeData.pnl,
            reason: closeData.reason ? `${signal.reason} → ${closeData.reason}` : signal.reason
          });
        } else {
          // SESSION 111: Track symbols with open positions
          if (signal.status === 'OPEN') {
            symbolsWithOpenPositions.add(signal.symbol?.toUpperCase());
          }
          processedSignals.push(signal);
        }
      });
      
      // SESSION 111: Filter out signals for symbols that already have an OPEN position
      // Only show the FIRST signal for each symbol (the OPEN one)
      const finalSignals = [];
      const seenOpenSymbols = new Set();
      
      // Sort by timestamp to process in order
      processedSignals.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      for (const signal of processedSignals) {
        const sym = signal.symbol?.toUpperCase();
        
        // If this symbol has an open position and this signal is NOT that open position
        if (symbolsWithOpenPositions.has(sym) && signal.status !== 'OPEN') {
          // Skip new signals that came AFTER an open position was established
          // But keep all CLOSED signals
          if (signal.status !== 'CLOSED') {
            continue;
          }
        }
        
        finalSignals.push(signal);
      }
      
      // Sort final list by timestamp descending (newest first)
      finalSignals.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setGlobalLog(finalSignals);
      
      // Also fetch active trades
      const [niftyActive, bnfActive] = await Promise.all([
        fetch(`${API}/api/signals/active?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/signals/active?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null)
      ]);
      
      setActiveTrades({
        NIFTY: niftyActive?.trade || null,
        BANKNIFTY: bnfActive?.trade || null
      });
      
    } catch (err) {
      console.error('Failed to fetch global signal log:', err);
    }
    setIsLoading(false);
  }, []);
  
  // Initial fetch and polling
  useEffect(() => {
    fetchMarketStatus();
    fetchGlobalLog();
    const interval = setInterval(fetchGlobalLog, 3000);
    const statusInterval = setInterval(fetchMarketStatus, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(statusInterval);
    };
  }, [fetchGlobalLog, fetchMarketStatus]);
  
  // Add new signals from strategy pulses
  // SESSION 91: Pre-filter BLOCKED signals to prevent UI thrashing
  // SESSION 113: FIX - Also check is_vetoed to prevent signal flash before filtering
  useEffect(() => {
    const addSignalFromPulse = (pulse, symbol) => {
      if (!pulse || pulse.action === 'NONE') return;
      
      const isVeto = pulse.is_vetoed;
      const isExecute = pulse.action === 'ENTER_LONG' || pulse.action === 'ENTER_SHORT';
      
      // SESSION 91: Only add EXECUTE signals to the log (skip VETOs entirely)
      // SESSION 113: CRITICAL FIX - Also skip if is_vetoed is true!
      // The pulse.action can be ENTER_LONG/SHORT even when the signal is BLOCKED
      // This was causing signals to flash briefly then disappear on API poll
      if (isExecute && !isVeto) {
        const newSignal = {
          id: `${symbol}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date().toISOString(),
          symbol: symbol,
          signal: pulse.direction === 'UP' ? 'BUY' : 'SELL',
          score: pulse.god_score,
          reason: pulse.action,
          status: 'OPEN'
        };
        
        setGlobalLog(prev => {
          const exists = prev.some(s => 
            s.symbol === symbol && 
            Math.abs(new Date(s.timestamp) - new Date(newSignal.timestamp)) < 2000
          );
          if (exists) return prev;
          
          const updated = [...prev, newSignal];
          updated.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          return updated;
        });
      }
    };
    
    addSignalFromPulse(niftyPulse, 'NIFTY');
    addSignalFromPulse(bankniftyPulse, 'BANKNIFTY');
  }, [niftyPulse, bankniftyPulse]);
  
  // SESSION 90: Calculate signal counts - exclude BLOCKED/VETO signals
  const validGlobalLog = globalLog.filter(s => 
    s.status !== 'BLOCKED' && s.status !== 'VETOED' && s.signal !== 'VETO'
  );
  const niftySignals = validGlobalLog.filter(s => s.symbol === 'NIFTY' || s.symbol === 'NIFTY-I').length;
  const bnfSignals = validGlobalLog.filter(s => s.symbol === 'BANKNIFTY' || s.symbol === 'BANKNIFTY-I').length;
  
  return (
    <div className="h-full flex flex-col p-3" data-testid="signal-command-center">
      {/* Header - Compact */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${marketStatus.isOpen ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-accent" />
            <span className="text-base font-bold text-foreground uppercase tracking-wider">
              Signal Log
            </span>
          </div>
          <span className="text-xs text-muted-foreground px-2 py-0.5 bg-surface/30 rounded">
            {marketStatus.isOpen ? 'LIVE' : (connectionStatus?.wsConnected ? 'REPLAY' : 'FROZEN')}
          </span>
          <span className="text-xs bg-surface/50 px-2 py-1 rounded text-muted-foreground">
            {validGlobalLog.length} signals
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-cyan-400 font-medium">NIFTY: {niftySignals}</span>
          <span className="text-amber-400 font-medium">BNF: {bnfSignals}</span>
          
          {/* === AUDIO TOGGLE BUTTON === */}
          <button 
            onClick={toggleAudio}
            className={`p-2 rounded transition-all duration-200 ${
              audioEnabled 
                ? 'bg-green-500/20 hover:bg-green-500/30 border border-green-500/50' 
                : 'bg-surface/30 hover:bg-surface/50 border border-border/30'
            }`}
            title={audioEnabled ? 'Audio Alerts ON - Click to Mute' : 'Audio Alerts OFF - Click to Enable'}
          >
            {audioEnabled ? (
              <Bell className="w-4 h-4 text-green-400" />
            ) : (
              <BellOff className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          
          <button 
            onClick={fetchGlobalLog}
            className="p-2 hover:bg-surface/50 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-muted-foreground ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Full-height Signal Log */}
      <div className="flex-1 min-h-0 bg-surface/20 rounded-lg border border-border/30">
        <GlobalSignalLogTable logs={validGlobalLog} isLoading={isLoading} />
      </div>
    </div>
  );
});

SignalCommandCenter.displayName = 'SignalCommandCenter';

export default SignalCommandCenter;
